// src/components/settings/FunctionalTestLibraryTab.tsx

/**
 * Functional test library tab for the Quantik Mind enterprise frontend.
 *
 * This component is responsible for:
 * - loading the project-specific functional test library
 * - adding new functional tests
 * - deleting single, multiple, or all tests
 * - importing functional tests from JSON files
 * - presenting upload results to the user
 */

import React, { useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Paper,
  Box,
  Typography,
  Divider,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import {
  useFunctionalLibrary,
} from '../../features/settings/hooks/useFunctionalLibrary';
import { DOC_PATHS, TEMPLATE_PATHS } from '../../utils/onboardingResources';
import Button from '../common/ActionButton';
import ResourceLink from '../common/ResourceLink';
import { LibraryTextCell, LibraryTagsCell, LibraryServiceCell } from './FunctionalLibraryTableCells';

/**
 * Props accepted by the functional test library tab.
 */
interface Props {
  /** Identifier of the current project. */
  projectId: string;
  focusTestId?: string;
}

/**
 * Render the functional test library management tab.
 *
 * @param props - Component props.
 * @returns The functional test library management tab.
 */
export default function FunctionalTestLibraryTab({ projectId, focusTestId }: Props) {
  const {
    rows,
    snackbar,
    setSnackbar,
    loading,
    addOpen,
    setAddOpen,
    confirmOpen,
    setConfirmOpen,
    confirmAllOpen,
    setConfirmAllOpen,
    deleteId,
    setDeleteId,
    uploadResults,
    uploadDialogOpen,
    setUploadDialogOpen,
    pageSize,
    setPageSize,
    selectedRows,
    setSelectedRows,
    newTestId,
    setNewTestId,
    newName,
    setNewName,
    newService,
    setNewService,
    newSuite,
    setNewSuite,
    newCriticality,
    setNewCriticality,
    newTags,
    setNewTags,
    newDuration,
    setNewDuration,
    sampleOnlyLibrary,
    deleteAllTests,
    addTest,
    uploadTests,
    confirmDelete,
  } = useFunctionalLibrary(projectId);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  /**
   * Open the hidden file input used for JSON uploads.
   */
  const handleJsonUpload = () => {
    jsonInputRef.current?.click();
  };

  /**
   * Import functional tests from a JSON file.
   *
   * @param event - File input change event.
   */
  const handleJsonFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (loadEvent) => {
      try {
        const json = JSON.parse(loadEvent.target?.result as string);

        if (!Array.isArray(json)) {
          setSnackbar('Invalid JSON format (expected array)');
          return;
        }

        await uploadTests(json);
      } catch (err) {
        console.error('Upload error:', err);
        setSnackbar('Upload failed. Compare your file with the template and format guide, then try again.');
      }
    };

    reader.readAsText(file);

    /**
     * Reset the file input so the same file can be selected again if needed.
     */
    event.target.value = '';
  };

  /**
   * Resolve the chip color used for a business criticality level.
   *
   * @param criticality - Business criticality value.
   * @returns Material UI chip color name.
   */
  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Grid column configuration for the functional test table.
   */
  const columns: GridColDef[] = [
    {
      field: 'test_id',
      headerName: 'Test ID',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => <LibraryTextCell value={params.value} />,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => <LibraryTextCell value={params.value} />,
    },
    {
      field: 'service',
      headerName: 'Service',
      flex: 1,
      minWidth: 190,
      renderCell: (params) => <LibraryServiceCell row={params.row} />,
    },
    {
      field: 'suite',
      headerName: 'Suite',
      width: 100,
    },
    {
      field: 'business_criticality',
      headerName: 'Criticality',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getCriticalityColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 220,
      renderCell: (params) => <LibraryTagsCell projectId={projectId} row={params.row} />,
    },
    {
      field: 'avg_duration_ms',
      headerName: 'Avg Duration (ms)',
      width: 120,
      valueFormatter: (params) => params.value ?? '-',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (params) => {
        try {
          return new Date(params.value).toLocaleString();
        } catch {
          return params.value;
        }
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          onClick={() => {
            setDeleteId(params.row.id);
            setConfirmOpen(true);
          }}
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  /**
   * Confirm deletion for either a single row or the current multi-selection.
   */
  return (
    <Paper sx={{ p: 3, maxWidth: 1400 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Functional Test Library
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Test
        </Button>

        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={handleJsonUpload}
        >
          Upload JSON
        </Button>

        <ResourceLink
          href={TEMPLATE_PATHS.functionalLibrary}
          target="_blank"
        >
          Download functional test library template
        </ResourceLink>

        <ResourceLink
          href={DOC_PATHS.dataUploadFormats}
          target="_blank"
        >
          View format guide
        </ResourceLink>

        {selectedRows.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}

        <Button
          variant="outlined"
          color="error"
          onClick={() => setConfirmAllOpen(true)}
        >
          Delete All
        </Button>

        <input
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          ref={jsonInputRef}
          onChange={handleJsonFileChange}
        />

        <Box sx={{ ml: 'auto' }}>
          <Chip
            label={`Total: ${rows.length} test${rows.length !== 1 ? 's' : ''}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      {sampleOnlyLibrary && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          A sample test library is active for this project. Upload your real test library when you are ready to move from guided exploration to production-relevant results.
        </Alert>
      )}

      {focusTestId && <Alert severity="info">Showing exact test ID: {focusTestId}. <Button href="/settings?tab=functional-tests">Show all tests</Button></Alert>}
      <DataGrid
        key={projectId}
        rows={focusTestId ? rows.filter(row => row.test_id === focusTestId) : rows}
        columns={columns}
        autoHeight
        rowHeight={52}
        sx={{
          '& .MuiDataGrid-cell': { overflow: 'hidden', whiteSpace: 'nowrap' },
          '& .MuiDataGrid-cellContent': { overflow: 'hidden', textOverflow: 'ellipsis' },
        }}
        pageSize={pageSize}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        rowsPerPageOptions={[10, 20, 50]}
        checkboxSelection
        disableSelectionOnClick
        loading={loading}
        onSelectionModelChange={(newSelection) => {
          setSelectedRows(newSelection as number[]);
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Functional Test</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
        >
          <TextField
            label="Test ID"
            value={newTestId}
            onChange={(event) => setNewTestId(event.target.value)}
            placeholder="test_login_001"
            helperText="Unique test identifier"
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Test Name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="Login with valid credentials"
            fullWidth
            required
          />
          <TextField
            label="Service"
            value={newService}
            onChange={(event) => setNewService(event.target.value)}
            placeholder="auth-service"
            fullWidth
            required
          />
          <TextField
            label="Suite"
            value={newSuite}
            onChange={(event) => setNewSuite(event.target.value)}
            placeholder="default"
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Business Criticality</InputLabel>
            <Select
              value={newCriticality}
              label="Business Criticality"
              onChange={(event) => setNewCriticality(event.target.value)}
            >
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Tags"
            value={newTags}
            onChange={(event) => setNewTags(event.target.value)}
            placeholder="smoke, regression, api"
            helperText="Comma-separated tags"
            fullWidth
          />
          <TextField
            label="Average Duration (ms)"
            value={newDuration}
            onChange={(event) => setNewDuration(event.target.value)}
            type="number"
            placeholder="1500"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button onClick={() => void addTest()} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            {deleteId !== null
              ? 'this test'
              : `${selectedRows.length} selected test${
                  selectedRows.length !== 1 ? 's' : ''
                }`}
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDelete()}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAllOpen} onClose={() => setConfirmAllOpen(false)}>
        <DialogTitle>Confirm Delete All</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>ALL functional tests</strong>?
          </Typography>
          <Typography color="error" fontWeight="bold" sx={{ mt: 1 }}>
            This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAllOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await deleteAllTests();
              setConfirmAllOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Results</DialogTitle>
        <DialogContent>
          {uploadResults && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="success">
                <Typography>
                  Successfully added: {uploadResults.added || 0}
                </Typography>
              </Alert>

              {(uploadResults.skipped || 0) > 0 && (
                <Alert severity="warning">
                  <Typography>
                    Skipped (duplicates): {uploadResults.skipped}
                  </Typography>
                </Alert>
              )}

              {(uploadResults.errors?.length || 0) > 0 && (
                <Alert severity="error">
                  <Typography fontWeight={700}>
                    The file could not be fully imported.
                  </Typography>
                  <Typography sx={{ mt: 0.5 }}>
                    Review the example rows below, then compare your file with the template and format guide.
                  </Typography>
                  <ResourceLink
                    href={DOC_PATHS.dataUploadFormats}
                    target="_blank"
                    sx={{ mt: 1 }}
                  >
                    View format guide
                  </ResourceLink>
                  <Typography sx={{ mt: 1 }}>Errors: {uploadResults.errors?.length}</Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    {uploadResults.errors?.map((err, index) => (
                      <li key={index}>
                        {typeof err === 'string'
                          ? err
                          : err?.error
                            ? `${err.index !== undefined ? `Row ${err.index}: ` : ''}${err.error}`
                            : 'Invalid row'}
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
